import { Flex, Grid, GridItem, Icon, Text } from '@chakra-ui/react'
import React from 'react'
import { useNavigate } from 'react-router'
import IconCalendar from '~/assets/svgs/icon-calendar.svg?react'
import IconInboundArrow from '~/assets/svgs/icon-inbound-arrow.svg?react'
import IconInternalArrow from '~/assets/svgs/icon-internal-arrow.svg?react'
import IconOutboundArrow from '~/assets/svgs/icon-outbound-arrow.svg?react'
import IconVoicemail from '~/assets/svgs/icon-voicemail.svg?react'
import { TextButton } from '~/components/shared/TextButton'
import { ApplicationRoutes } from '~/hooks/useRouting'
import {
  CallDetailsRow,
  CallDetailsRowItem,
} from '~/pages/Calls/CallDetails/CallDetailsRow'
import { ContactInfoCard } from '~/pages/Calls/CallDetails/ContactInfoCard'
import { CallTranscriptSummarySection } from '~/pages/Calls/CallDetails/sections/CallTranscriptSummarySection'
import {
  CallTarget,
  getCallTargetData,
} from '~/pages/Calls/data/CallDetailData'
import { textStyleKeys } from '~/themes/textStyles'
import { hasValue } from '~/utils/common'
import { formatSeconds } from '~/utils/formatter'

import { formatDateString, formatTimeString } from '@mangovoice/core'
import { toNounCasing } from '@mangovoice/core'
import {
  ApiError,
  CallDirection,
  CallInstance,
  VoicemailInstance,
} from '@mangovoice/types'

import { BouncingEllipsis } from './BouncingEllipsis'
import { TRANSCRIPT_SUMMARY_NOT_AVAILABLE } from './CallDetailsAnalyticsContent'
import { VoicemailTranscriptSection } from './sections/VoicemailTranscriptSection'

interface CallDetailsOverviewTabProps {
  callInstance?: CallInstance
  transcriptSummary?: string
  answerTimestampInMs?: number
  overallCustomerScore?: number
  overallAgentScore?: number
  isLoadingTranscripts?: boolean
  voicemail?: VoicemailInstance
  voicemailError?: ApiError
  isVoicemailLoading?: boolean
}

export const CallDetailsOverviewTab = ({
  callInstance,
  transcriptSummary,
  answerTimestampInMs,
  overallCustomerScore,
  overallAgentScore,
  isLoadingTranscripts,
  voicemail,
  voicemailError,
  isVoicemailLoading,
}: CallDetailsOverviewTabProps) => {
  let outcomeIcon: SVGComponent | undefined
  switch (callInstance?.direction) {
    case CallDirection.Inbound:
      outcomeIcon = IconInboundArrow
      break
    case CallDirection.Outbound:
      outcomeIcon = IconOutboundArrow
      break
    case CallDirection.Internal:
      outcomeIcon = IconInternalArrow
      break
  }
  const isTranscriptionInProgress =
    callInstance?.transcription_status === 'IN_PROGRESS'
  const hasTranscript =
    transcriptSummary && transcriptSummary !== TRANSCRIPT_SUMMARY_NOT_AVAILABLE

  const toTargetData = callInstance
    ? getCallTargetData(callInstance, CallTarget.To)
    : undefined
  const fromTargetData = callInstance
    ? getCallTargetData(callInstance, CallTarget.From)
    : undefined

  const navigate = useNavigate()

  const hasVoicemail = hasValue(callInstance?.voicemail_id)
  const handleVoicemailClick = () => {
    if (!callInstance) return
    navigate(`${ApplicationRoutes.VOICEMAIL}/${callInstance.voicemail_id}`)
  }

  const isCallInProgress = callInstance && !callInstance.ended_at

  return (
    <Flex flexDirection="column" gap="1.25rem" width="100%">
      {(hasTranscript || isTranscriptionInProgress) && (
        <CallDetailsRow>
          <CallTranscriptSummarySection
            callInstance={callInstance}
            isLoading={isLoadingTranscripts ?? false}
            summaryText={transcriptSummary}
            overallCustomerScore={overallCustomerScore ?? 0}
            overallAgentScore={overallAgentScore ?? 0}
            voicemail={voicemail}
            voicemailError={voicemailError}
            isVoicemailLoading={isVoicemailLoading}
            onVoicemailClick={handleVoicemailClick}
          />
        </CallDetailsRow>
      )}
      {!hasTranscript && !isTranscriptionInProgress && hasVoicemail && (
        <CallDetailsRow>
          <VoicemailTranscriptSection
            voicemail={voicemail}
            voicemailError={voicemailError}
            isVoicemailLoading={isVoicemailLoading}
            onVoicemailClick={handleVoicemailClick}
            isToolTip={false}
          />
        </CallDetailsRow>
      )}
      <CallDetailsRow>
        {fromTargetData && (
          <ContactInfoCard
            callTarget={CallTarget.From}
            title={fromTargetData.titleValue}
            subtitle={fromTargetData.subtitleValue}
            contact={fromTargetData.contact}
            number={fromTargetData.number}
            isExtension={fromTargetData.isExtension}
            location={fromTargetData.location}
            locationIsTitle={fromTargetData.locationIsTitle}
          />
        )}
        {toTargetData && (
          <ContactInfoCard
            callTarget={CallTarget.To}
            title={toTargetData.titleValue}
            subtitle={toTargetData.subtitleValue}
            contact={toTargetData.contact}
            number={toTargetData.number}
            isExtension={toTargetData.isExtension}
            location={toTargetData.location}
            locationIsTitle={toTargetData.locationIsTitle}
          />
        )}
      </CallDetailsRow>
      <CallDetailsRow>
        <CallDetailsRowItem>
          <Text textStyle={textStyleKeys.Body1_Bold}>Call Timeline</Text>
          <Flex
            data-testid="call-details-row"
            justifyContent="space-between"
            alignItems="center"
            alignSelf="stretch"
          >
            <Flex
              gap="1rem"
              alignItems="center"
              data-testid="call-details-date-direction-label-container"
            >
              <Flex alignItems="center" gap="0.5rem">
                <Icon as={IconCalendar} boxSize="1rem" />
                <Text
                  textStyle={textStyleKeys.Body2_Semibold}
                  lineHeight="1.25rem"
                >
                  {formatDateString(callInstance!.started_at)}
                </Text>
              </Flex>
              <Flex alignItems="center" gap="0.25rem">
                <Icon
                  as={outcomeIcon}
                  boxSize="1rem"
                  sx={{
                    color: isCallInProgress
                      ? 'blue.900'
                      : callInstance!.is_missed
                        ? 'red.800'
                        : 'initial',
                  }}
                />
                <Text
                  textStyle={textStyleKeys.Body2_Semibold}
                  lineHeight="1.25rem"
                  color={
                    isCallInProgress
                      ? 'blue.900'
                      : callInstance!.is_missed
                        ? 'red.800'
                        : 'initial'
                  }
                >
                  {toNounCasing(callInstance!.direction) + ', '}
                  {isCallInProgress
                    ? 'In Progress'
                    : callInstance!.is_missed
                      ? 'Missed'
                      : 'Answered'}
                </Text>
              </Flex>
            </Flex>
            <Flex>
              {hasVoicemail && !transcriptSummary && (
                <TextButton
                  leftIcon={IconVoicemail}
                  onLinkClick={handleVoicemailClick}
                >
                  Voicemail Recorded
                </TextButton>
              )}
            </Flex>
          </Flex>
          <Grid templateColumns="repeat(4, 1fr)" gap="0.5rem" w="full">
            <GridItem
              data-testid="call-details-block"
              colSpan={1}
              height="3.5rem"
              py="0.5rem"
              px="0.75rem"
              borderRadius="0.5rem"
              bg="gray.400"
            >
              <Text textStyle={textStyleKeys.Body1_Bold}>
                {formatTimeString(callInstance!.started_at)}
              </Text>
              <Text textStyle={textStyleKeys.Body3} lineHeight="1rem">
                Started
              </Text>
            </GridItem>
            {answerTimestampInMs && (
              <GridItem
                data-testid="call-details-block"
                colSpan={1}
                height="3.5rem"
                py="0.5rem"
                px="0.75rem"
                borderRadius="0.5rem"
                bg="gray.400"
              >
                <Text textStyle={textStyleKeys.Body1_Bold}>
                  {formatSeconds(answerTimestampInMs / 1000)}
                </Text>
                <Text textStyle={textStyleKeys.Body3} lineHeight="1rem">
                  Answer Time
                </Text>
              </GridItem>
            )}
            <GridItem
              data-testid="call-details-block"
              display="flex"
              colSpan={answerTimestampInMs ? 1 : 2}
              height="3.5rem"
              py="0.5rem"
              px="0.75rem"
              flexDirection="column"
              justifyContent="center"
              alignItems={answerTimestampInMs ? 'flex-start' : 'center'}
              borderRadius="0.5rem"
              bg="gray.400"
            >
              <Text textStyle={textStyleKeys.Body1_Bold}>
                {isCallInProgress ? (
                  <BouncingEllipsis />
                ) : (
                  formatSeconds(callInstance!.duration_in_seconds)
                )}
              </Text>
              <Text textStyle={textStyleKeys.Body3} lineHeight="1rem">
                Call Duration
              </Text>
            </GridItem>
            <GridItem
              data-testid="call-details-block"
              colSpan={1}
              height="3.5rem"
              py="0.5rem"
              px="0.75rem"
              borderRadius="0.5rem"
              bg="gray.400"
            >
              <Text textStyle={textStyleKeys.Body1_Bold}>
                {callInstance?.ended_at ? (
                  formatTimeString(callInstance.ended_at)
                ) : (
                  <BouncingEllipsis />
                )}
              </Text>
              <Text textStyle={textStyleKeys.Body3} lineHeight="1rem">
                Ended
              </Text>
            </GridItem>
          </Grid>
        </CallDetailsRowItem>
      </CallDetailsRow>
      {callInstance?.call_flow_entries &&
        callInstance.call_flow_entries.length > 0 && (
          <CallDetailsRow>
            <CallDetailsRowItem>
              <Text textStyle={textStyleKeys.Body1_Bold}>Call Flow</Text>
              <Flex
                data-testid="call-flow-data-container"
                flexDirection="column"
                alignItems="flex-start"
                alignSelf="stretch"
              >
                {
                  //TODO: Fix typing on call_flow_entries
                  callInstance!.call_flow_entries.map((entry, index) => {
                    return (
                      <Text key={index} textStyle={textStyleKeys.Body1}>
                        {entry.label}: {entry.value}
                      </Text>
                    )
                  })
                }
              </Flex>
            </CallDetailsRowItem>
          </CallDetailsRow>
        )}
    </Flex>
  )
}
